import SectionHeading from '@/components/base/SectionHeading';
import { useInView } from '@/hooks/useInView';
import { careerBenefits } from '@/mocks/careers';

export default function WhyWorkWithUs() {
  const { ref, inView } = useInView<HTMLDivElement>(0.15);

  return (
    <section className="py-20 md:py-28 bg-background-100">
      <div className="mx-auto max-w-[1440px] px-4 md:px-8">
        <SectionHeading
          eyebrow="Why Work With Us"
          title="A Place Where Good People Do Great Work"
          description="We invest in our people because dependable solutions are built by capable, supported teams."
        />

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {careerBenefits.map((benefit, idx) => (
            <BenefitCard key={benefit.title} benefit={benefit} delay={idx * 70} />
          ))}
        </div>

        <div
          ref={ref}
          className={`mt-12 rounded-3xl overflow-hidden border border-background-200 bg-background-50 ${
            inView ? 'animate-fade-up' : 'opacity-0'
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch">
            <div className="h-64 lg:h-auto">
              <img
                src={`${import.meta.env.BASE_URL}images/9fad92ce266a3519.jpg`}
                alt="Life at John Holt PLC"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="p-7 md:p-10 flex flex-col justify-center">
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-accent-600">
                <span className="w-6 h-px bg-accent-600"></span>
                Our Culture
              </span>
              <h3 className="font-heading mt-4 text-2xl md:text-3xl font-semibold text-foreground-950 leading-tight">
                Heritage That Values People
              </h3>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
                For generations, John Holt PLC has been built on the skill, integrity and dedication of its people. We
                believe in doing the job properly, looking after one another and giving every team member the room to
                grow.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Mentorship from experienced engineers and specialists',
                  'Training that keeps your skills sharp and relevant',
                  'A safe, respectful and inclusive working environment',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground-700 leading-snug">
                    <i className="ri-check-line text-primary-600 mt-0.5 shrink-0"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function BenefitCard({
  benefit,
  delay,
}: {
  benefit: (typeof careerBenefits)[number];
  delay: number;
}) {
  const { ref, inView } = useInView<HTMLDivElement>(0.15);
  return (
    <div
      ref={ref}
      style={{ animationDelay: `${delay}ms` }}
      className={`bg-background-50 border border-background-200 rounded-2xl p-6 h-full hover:border-primary-300 transition-colors ${
        inView ? 'animate-fade-up' : 'opacity-0'
      }`}
    >
      <span className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-600">
        <i className={`${benefit.icon} text-xl`}></i>
      </span>
      <h3 className="font-heading mt-5 text-base font-semibold text-foreground-950 leading-snug">{benefit.title}</h3>
      <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed">{benefit.description}</p>
    </div>
  );
}